import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/ui/base/button";
import { ConfirmDialog } from "@/ui/ConfirmDialog";
import { Download, Edit, History, Clock, Trash2 } from "lucide-react";
import type { DocArticleCompleto } from "@/lib/useDocs";
import { formatDate } from "@/lib/dateFormat";

interface DocsViewerProps {
  article: DocArticleCompleto;
  isAdmin?: boolean;
  onEdit?: () => void;
  onShowHistory?: () => void;
  onDelete?: () => void;
}

export function DocsViewer({ article, isAdmin, onEdit, onShowHistory, onDelete }: DocsViewerProps) {
  const handleDownload = () => {
    const blob = new Blob([article.conteudo_markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${article.slug}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex-1 overflow-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-6 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-foreground">{article.titulo}</h1>
          {article.updated_at && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Clock className="h-3 w-3" />
              Atualizado em {formatDate(article.updated_at)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && onShowHistory && (
            <Button variant="outline" size="sm" onClick={onShowHistory} className="gap-1.5">
              <History className="h-3.5 w-3.5" /> Histórico
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5">
            <Download className="h-3.5 w-3.5" /> Baixar .md
          </Button>
          {isAdmin && onEdit && (
            <Button size="sm" onClick={onEdit} className="gap-1.5">
              <Edit className="h-3.5 w-3.5" /> Editar
            </Button>
          )}
          {isAdmin && onDelete && (
            <ConfirmDialog
              trigger={
                <Button variant="outline" size="sm" className="gap-1.5 text-destructive-text hover:text-destructive-text" aria-label="Excluir artigo">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              }
              title="Excluir artigo"
              description={`Tem certeza que deseja excluir "${article.titulo}"? Esta ação não pode ser desfeita.`}
              confirmLabel="Excluir"
              variant="destructive"
              onConfirm={onDelete}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6 max-w-4xl">
        <article className="prose prose-sm dark:prose-invert max-w-none
          prose-headings:text-foreground prose-headings:font-bold
          prose-h1:text-2xl prose-h1:border-b prose-h1:border-border prose-h1:pb-3 prose-h1:mb-6
          prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3
          prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2
          prose-p:text-foreground/80 prose-p:leading-relaxed
          prose-a:text-primary prose-a:no-underline hover:prose-a:underline
          prose-strong:text-foreground prose-strong:font-semibold
          prose-code:text-primary prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs prose-code:font-mono
          prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:rounded-xl
          prose-table:border-collapse
          prose-th:bg-muted prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:text-xs prose-th:font-semibold prose-th:text-foreground
          prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2 prose-td:text-sm
          prose-li:text-foreground/80
          prose-hr:border-border
        ">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {article.conteudo_markdown || "*Sem conteúdo ainda. Clique em Editar para começar.*"}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
}
